package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.isPermissionDenied
import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.data.dto.finance.CostDto
import com.evoapps.evoshift.data.dto.roster.RosterDto
import com.evoapps.evoshift.data.dto.roster.RosterStatusDto
import com.evoapps.evoshift.data.dto.system.MetadataDto
import com.evoapps.evoshift.data.mapper.RosterMapper
import com.evoapps.evoshift.data.mapper.RosterStatusMapper
import com.evoapps.evoshift.domain.model.MaterializedCost
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.model.RosterPublication
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.map
import com.evoapps.evoshift.domain.util.AppClock.now
import com.evoapps.evoshift.data.dto.finance.CostSourceTypeDto

class RosterRepositoryImpl(
    private val firestore: AppFirestore,
    private val rosterMapper: RosterMapper,
    private val rosterStatusMapper: RosterStatusMapper
) : RosterRepository {

    override suspend fun getRoster(rosterId: String): Result<Roster> {
        return try {
            val doc = firestore.collection("rosters").document(rosterId).get()
            if (doc.exists) {
                Result.success(rosterMapper.toDomain(doc.data()))
            } else {
                Result.failure(Exception("Roster not found"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override fun observeRoster(rosterId: String): Flow<Roster> {
        return firestore.collection("rosters")
            .document(rosterId)
            .safeSnapshots
            .map { doc -> rosterMapper.toDomain(doc.data()) }
            .distinctUntilChanged()
    }

    override fun observeRosters(workplaceId: String): Flow<List<Roster>> {
        return firestore.collection("rosters")
            .where { "workplaceId" equalTo workplaceId }
            .snapshots
            .map { snapshot ->
                snapshot.documents.map { doc -> rosterMapper.toDomain(doc.data()) }
            }
            .catch { e ->
                if (e.isPermissionDenied()) {
                    emit(emptyList())
                } else {
                    throw e
                }
            }
            .distinctUntilChanged()
    }

    override suspend fun createRoster(workplaceId: String, month: Int, year: Int): Result<String> {
        return try {
            val rosterId = RosterIds.of(workplaceId, year, month)
            val initialDto = RosterDto(
                id = rosterId,
                workplaceId = workplaceId,
                month = month,
                year = year,
                status = RosterStatusDto.SKELETON,
                currentVersion = 1,
                metadata = MetadataDto(
                    createdAt = now().toString(),
                    createdBy = "SYSTEM", 
                    updatedAt = null,
                    updatedBy = null
                )
            )

            firestore.collection("rosters").document(rosterId).set(initialDto)
            Result.success(rosterId)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun deleteRoster(rosterId: String): Result<Unit> {
        return try {
            firestore.collection("rosters").document(rosterId).delete()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun publishRoster(rosterId: String): Result<Unit> {
        return updateRosterStatus(rosterId, RosterStatus.PUBLISHED)
    }

    override suspend fun publishRoster(rosterId: String, publication: RosterPublication): Result<Unit> = runCatching {
        firestore.collection("rosters").document(rosterId).updateFields {
            "status" to rosterStatusMapper.toDto(RosterStatus.PUBLISHED)
            "currentVersion" to publication.version
            // SH-271: whom onRosterPublished e-mails on a republish (null: the first publish, everyone).
            "lastPublishChangedUserIds" to publication.changedUserIds
        }
    }

    override suspend fun updateRosterStatus(rosterId: String, status: RosterStatus): Result<Unit> {
        return try {
            val dtoStatus = rosterStatusMapper.toDto(status)
            firestore.collection("rosters").document(rosterId).updateFields {
                "status" to dtoStatus
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun materializeCosts(
        rosterId: String,
        costs: List<MaterializedCost>
    ): Result<Unit> {
        return try {
            val batch = firestore.batch()
            val collectionRef = firestore.collection("rosters")
                .document(rosterId)
                .collection("costs")

            costs.forEach { cost ->
                val newDocRef = collectionRef.document
                // A snapshot of the rule as it is today; later rule changes leave this month alone.
                val costDto = CostDto(
                    id = newDocRef.id,
                    name = cost.rule.name,
                    amount = cost.amount,
                    currency = cost.rule.currency,
                    date = cost.date,
                    sourceType = CostSourceTypeDto.GENERATED_FROM_RULE,
                    originRuleId = cost.rule.id,
                    // SH-87: the rule's kind travels into the month's ledger.
                    kind = cost.rule.kind.name,
                    // SH-195: a monthly charge is spread over the month's days, a weekly one stays on its day.
                    frequency = cost.rule.frequency.name
                )
                batch.set(newDocRef, costDto)
            }
            batch.commit()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun linkAvailabilityRequest(
        rosterId: String,
        availabilityRequestId: String
    ): Result<Unit> {
        return try {
            firestore.collection("rosters").document(rosterId).updateFields {
                "availabilityRequestId" to availabilityRequestId
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}